import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../../services/api';

// Get cookie value by name
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop().split(';').shift();
  }
  return null;
};

// Helper to get current language for API requests (site is English-only)
const getCurrentLanguage = () => 'en';

// Register user
export const register = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/register', { ...userData, lang: getCurrentLanguage() });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Registration failed');
    }
  }
);

// Login user
export const login = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/login', { ...credentials, lang: getCurrentLanguage() });
      const { user, case: visaCase } = response.data.data;
      return { user, case: visaCase };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Login failed');
    }
  }
);

// Google OAuth login
export const googleLogin = createAsyncThunk(
  'auth/googleLogin',
  async (googleData, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/google', googleData);
      const { user, case: visaCase } = response.data.data;
      return { user, case: visaCase };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Google login failed');
    }
  }
);

// Verify email
export const verifyEmail = createAsyncThunk(
  'auth/verifyEmail',
  async (token, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/verify-email', { token, lang: getCurrentLanguage() });
      const { user, case: visaCase } = response.data.data;
      return { user, case: visaCase };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Email verification failed');
    }
  }
);

// Resend verification email
export const resendVerification = createAsyncThunk(
  'auth/resendVerification',
  async (email, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/resend-verification', { email, lang: getCurrentLanguage() });
      return response.data.message;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to resend verification');
    }
  }
);

// Request password reset
export const requestPasswordReset = createAsyncThunk(
  'auth/requestPasswordReset',
  async (email, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/forgot-password', { email, lang: getCurrentLanguage() });
      return response.data.message;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to request password reset');
    }
  }
);

// Reset password
export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ token, newPassword }, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/reset-password', { token, newPassword, lang: getCurrentLanguage() });
      return response.data.message;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reset password');
    }
  }
);

// Verify authentication status on app startup
export const verifyAuth = createAsyncThunk(
  'auth/verifyAuth',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/auth/profile', {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      const { user, case: visaCase } = response.data.data;
      return { user, case: visaCase };
    } catch (error) {
      document.cookie = 'auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie = 'user_hp=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie = 'user_public=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      return rejectWithValue(error.response?.data?.message || 'Authentication verification failed');
    }
  }
);

// Fetch user profile (API call)
export const fetchProfile = createAsyncThunk(
  'auth/fetchProfile',
  async (_, { rejectWithValue }) => {
    // Log to see who's calling fetchProfile
    // console.trace('🔥🔥🔥 fetchProfile CALLED - check stack trace');
    try {
      const response = await api.get('/auth/profile', {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      const { user } = response.data.data;
      return user;
    } catch (error) {
      console.error('❌ fetchProfile ERROR:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch profile');
    }
  }
);

// Update user profile (API call)
export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      
      const response = await api.put('/auth/profile', profileData);
      
      
      const { user } = response.data.data;
      // Cookie updated by backend automatically
      return user;
    } catch (error) {
      console.error('❌ authSlice updateProfile - Error:', error);
      return rejectWithValue(error.response?.data?.message || 'Profile update failed');
    }
  }
);

// Change password
export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async ({ currentPassword, newPassword }, { rejectWithValue }) => {
    try {
      const response = await api.put('/auth/password', {
        currentPassword,
        newPassword,
        lang: getCurrentLanguage()
      });
      return response.data.message;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to change password');
    }
  }
);

// Logout
export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await api.post('/auth/logout'); // Clears cookies on server
      
      // Also clear cookies on client side (belt and suspenders approach)
      document.cookie = 'auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie = 'user_hp=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie = 'user_public=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

      return null;
    } catch (error) {
      // Even if API call fails, clear cookies locally
      document.cookie = 'auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie = 'user_hp=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie = 'user_public=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      
      return rejectWithValue(error.response?.data?.message || 'Logout failed');
    }
  }
);

// Initialize auth state from cookie
// NOTE: user_hp is httpOnly (JS can't read it). user_public is the JS-readable cookie.
const initializeAuth = () => {
  const userCookie = getCookie('user_public');

  if (userCookie) {
    try {
      const user = JSON.parse(decodeURIComponent(userCookie));
      // Token is in HTTP-only cookie auth_token, browser sends automatically
      // Set isAuthenticated to false initially - will be verified by verifyAuth
      return { user, isAuthenticated: false, needsVerification: true };
    } catch (error) {
      console.error('Failed to parse user cookie:', error);
      return { user: null, isAuthenticated: false, needsVerification: false };
    }
  }

  return { user: null, isAuthenticated: false, needsVerification: false };
};

const initialAuthState = initializeAuth();

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: initialAuthState.user,
    isAuthenticated: initialAuthState.isAuthenticated,
    needsVerification: initialAuthState.needsVerification,
    loading: false,
    error: null,
    successMessage: null,
    profileFetched: false, // Flag to prevent multiple fetches
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    updateUser: (state, action) => {
      state.user = { ...state.user, ...action.payload };
      // Cookie is updated by backend on API calls
    },
  },
  extraReducers: (builder) => {
    builder
      // Verify Auth
      .addCase(verifyAuth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.needsVerification = false;
        state.user = action.payload?.user ?? action.payload;
        state.error = null;
      })
      .addCase(verifyAuth.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.needsVerification = false;
        state.user = null;
        state.error = null; // Don't show error for failed verification
      })
      // Register
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = 'Account created successfully! Check your email.';
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.needsVerification = false;
        state.user = action.payload.user;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Google Login
      .addCase(googleLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(googleLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.error = null;
      })
      .addCase(googleLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Verify Email
      .addCase(verifyEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.successMessage = 'Email verificat cu succes!';
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Resend Verification
      .addCase(resendVerification.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resendVerification.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload;
      })
      .addCase(resendVerification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Request Password Reset
      .addCase(requestPasswordReset.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(requestPasswordReset.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload;
      })
      .addCase(requestPasswordReset.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Reset Password
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Profile
      .addCase(fetchProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload?.user ?? action.payload;
        state.profileFetched = true;
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.profileFetched = true; // Prevent infinite retry loop
      })
      // Update Profile
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.successMessage = 'Profile actualizat cu succes!';
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Change Password
      .addCase(changePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.needsVerification = false;
        state.user = null;
        state.loading = false;
        state.error = null;
        state.profileFetched = false; // Reset flag on logout
      })
      // Listen to cart/validatePromocode to update user with new promocode
      .addMatcher(
        (action) => action.type === 'cart/validatePromocode/fulfilled',
        (state, action) => {
          // Update user with fresh data from backend if provided
          if (action.payload?.user) {
            state.user = action.payload.user;
            // NO cookie update here! Backend already updates cookie via Set-Cookie header
          } else {
            console.log('❌ No user in payload!');
          }
        }
      );
  },
});

export const { clearError, clearSuccessMessage, updateUser } = authSlice.actions;
export default authSlice.reducer;

